import {
  BlockPlaybook,
  BotSkill,
  BotSkillConfiguration,
  BotSkillType,
  Playbook,
  TargetCondition,
  Team,
  User,
} from '@driftt/api-types'
import { validator } from '@driftt/conditions-core'
import { pluralize } from 'humanize-plus'
import { isEmpty, keys, path, pathOr, pickBy, toString, type } from 'ramda'

import { hasGates } from 'utils/access'

const CONDITIONS_TO_VALIDATE: Record<
  string,
  (value: string, operator: string) => string | undefined
> = {
  cookie: function validateCookie(value: string, operator: string) {
    if (!validator.isValidCookie(value, operator)) {
      return 'Invalid cookie'
    }
  },
  url: function validateUrl(value: string, operator: string) {
    if (!validator.isValidUrl(value, operator)) {
      return 'Invalid URL'
    }
  },
  urlHost: function validateUrlHost(value: string, operator: string) {
    if (!validator.isValidUrlHost(value, operator)) {
      return 'Invalid URL host'
    }
  },
  urlPath: function validateUrlPath(value: string, operator: string) {
    if (!validator.isValidUrlPath(value, operator)) {
      return 'Invalid URL path'
    }
  },
  urlQuery: function validateUrlQuery(value: string, operator: string) {
    if (!validator.isValidUrlParam(value, operator)) {
      return 'Invalid query param'
    }
  },
}

export interface ValidationResult {
  source: string
  nodeLabel: string
  message: string
}

interface RoutingConfiguration {
  userId: number
  userIds: number[]
  type: string
  teamId: number
}

export interface MeetingSkillConfiguration extends BotSkillConfiguration {
  primaryRoutingConfig: RoutingConfiguration
}

export const BLOCK_OUTDATED_MESSAGE = 'Update required for'

export const getMajorVersionFromBlock = (block: Playbook | BlockPlaybook) => {
  const latestBlockVersion = block.meta.blockVersion
  return path([0], latestBlockVersion.split('.'))
}

function validateBlockSkill(
  skill: BotSkill,
  playbookBlock: Playbook,
  addBlockSkillError: (blockName: string, numPaths?: number) => void
) {
  const currentBlockMajorVersion = toString(
    path(['configuration', 'version', 'major'], skill)
  )

  const blockName = playbookBlock.name

  if (currentBlockMajorVersion !== getMajorVersionFromBlock(playbookBlock)) {
    addBlockSkillError(blockName)
  } else {
    const { blockOutputConfigurations = {} } = skill.configuration
    const outputKeys = keys(blockOutputConfigurations)
    let numDeletedPaths = 0
    for (let outputKey of outputKeys) {
      const outputConfig = blockOutputConfigurations[outputKey]
      if (outputConfig.isDeleted) {
        numDeletedPaths++
      }
    }
    if (numDeletedPaths > 0) {
      addBlockSkillError(blockName, numDeletedPaths)
    }
  }
}

export function isEmptyOrWhiteSpace(stringValue: string): boolean {
  return stringValue.replace(' ', '').length < 1
}

export function validateSkills(
  skills: BotSkill[] = [],
  displayOrder: string[] = [],
  source: 'BUILDER' | 'EXPERIMENT',
  blocksById: { [key: string]: Playbook } = {},
  connectedCalendars: number[] = [],
  orgTeams: Team[],
  orgUsers: User[]
): Record<string, ValidationResult> {
  const draft: Record<string, ValidationResult> = {}

  function indexOfNode(nodeId: string) {
    return displayOrder.indexOf(nodeId)
  }

  skills.forEach((skill) => {
    const answers = pathOr([], ['configuration', 'answers'], skill)

    const conditionSets = pathOr<{ isEmpty: boolean; outputKey: string }[]>(
      [],
      ['configuration', 'nodeConditionSetsCollection'],
      skill
    )
    const nodeId = skill.id

    const addEmptyPathError = (id: string, nodeType: string) => {
      if (isEmpty(draft[nodeId] || {})) {
        draft[nodeId] = {
          source: source,
          nodeLabel: `${nodeType} ${indexOfNode(nodeId) + 1}`,
          message: 'Empty path in',
        }
      } else {
        draft[nodeId].message = 'Empty paths in'
      }
    }

    const addBlockSkillError = (blockName: string, numDeletedPaths = 0) => {
      draft[nodeId] = {
        source: source,
        nodeLabel: `${blockName}`,
        message: numDeletedPaths
          ? `Delete ${pluralize(numDeletedPaths, 'path')} in`
          : BLOCK_OUTDATED_MESSAGE,
      }
    }

    const addBlockSkillDeletedError = () => {
      draft[nodeId] = {
        source: source,
        nodeLabel: '',
        message: 'Remove deleted block from playbook',
      }
    }

    const addLegacyBlockError = () => {
      draft[nodeId] = {
        source: source,
        nodeLabel: '',
        message: 'Invalid block skill in playbook',
      }
    }

    if (skill.type === BotSkillType.QUESTION) {
      answers.forEach(({ id, type }) => {
        if (type === 'EMPTY_PATH') {
          addEmptyPathError(id, 'Question')
        }
      })
    }

    if (skill.type === BotSkillType.CONDITIONAL) {
      conditionSets.forEach((condition) => {
        if (condition.isEmpty) {
          addEmptyPathError(condition.outputKey, 'Condition')
        }
      })
    }

    if (
      skill.type === BotSkillType.WEBHOOK &&
      skill?.configuration?.definition?.urlTemplate === ''
    ) {
      draft[nodeId] = {
        source: source,
        nodeLabel: '',
        message: 'Webhook not setup',
      }
    }

    if (skill.type === BotSkillType.EMAIL && !hasGates('send_email_skill')) {
      draft[nodeId] = {
        source: source,
        nodeLabel: '',
        message: 'Invalid skill in playbook',
      }
    }

    if (skill.type === BotSkillType.BLOCK_VERSION) {
      const { blockId } = skill.configuration
      const blockSkillConfigurations = pathOr(
        {},
        ['configuration', 'blockOutputConfigurations'],
        skill
      )
      const getEmptyOutputs = (val: { isEmpty: boolean }, key: string) =>
        !!val.isEmpty
      const emptyBlockOutputs: { [key: string]: { isEmpty: boolean } } = pickBy(
        getEmptyOutputs,
        blockSkillConfigurations
      )
      for (const emptyBlockOutputKey in emptyBlockOutputs) {
        addEmptyPathError(emptyBlockOutputKey, 'Block')
      }
      const latestPlaybookBlock = blockId ? blocksById[blockId] : null
      if (latestPlaybookBlock) {
        validateBlockSkill(skill, latestPlaybookBlock, addBlockSkillError)
      } else if (!isEmpty(blocksById) && !latestPlaybookBlock) {
        addBlockSkillDeletedError()
      }
    }

    if (skill.type === BotSkillType.BLOCK) {
      addLegacyBlockError()
    }

    if (skill.type === BotSkillType.SCHEDULING) {
      if (connectedCalendars.length > 0 && orgTeams) {
        const { primaryRoutingConfig } =
          skill.configuration as MeetingSkillConfiguration
        const { type, userId, userIds, teamId } = primaryRoutingConfig
        let userIdsToCheck = userIds
        switch (type) {
          case 'TEAM':
          case 'GROUP':
            //only break the playbook if entire team/group is missing calendars
            if (userIds.length < 1) {
              //grab userIds from the team
              const selectedTeam = orgTeams.find((team) => team.id === teamId)
              userIdsToCheck = selectedTeam?.members ?? userIdsToCheck
            }
            if (userIdsToCheck.length < 1) {
              break
            }
            const usersDisconnected = userIdsToCheck.every(
              (userId) => !connectedCalendars.includes(userId)
            )
            if (usersDisconnected) {
              draft[nodeId] = {
                source: source,
                nodeLabel: '',
                message:
                  'Selected users in meeting skill do not have connected calendars',
              }
            }
            break
          case 'SINGLE':
            const userDisconnected = !connectedCalendars.includes(userId)
            if (userDisconnected) {
              draft[nodeId] = {
                source: source,
                nodeLabel: '',
                message:
                  'Selected user in meeting skill does not have connected calendars',
              }
            }

            break
        }
      }
    }
  })

  return draft
}

export function validateTargetingConditions(
  display: TargetCondition[][] = []
): Record<string, ValidationResult> {
  const draft: Record<string, ValidationResult> = {}
  const existingErrors: Record<string, string> = {}

  display.forEach((conditionGroup, groupIndex) => {
    conditionGroup.forEach((condition, conditionIndex) => {
      const { field, operator, value = [] } = condition
      const conditionToValidate = CONDITIONS_TO_VALIDATE[field] || null

      if (conditionToValidate) {
        if (type(value) === 'Array') {
          ;(value as string[]).forEach((val, vIndex) => {
            const id = `${groupIndex}-${conditionIndex}-${vIndex}`
            const message = conditionToValidate(val, operator)

            if (!message) {
              return
            }

            if (existingErrors[message]) {
              draft[existingErrors[message]].message = message + 's'
            } else {
              existingErrors[message] = id
              draft[id] = {
                source: 'AUDIENCE',
                nodeLabel: 'Targeting tab',
                message,
              }
            }
          })
        }
      }
    })
  })

  return draft
}
